import React, { useState, useEffect } from "react";
import axios from "axios";
import _ from 'lodash';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert
} from "@mui/material";

export default function EditLicenseDialog({ open, onClose, fieldToEdit, license, onUpdate, userData }) {
  const [formData, setFormData] = useState({
    ownerUsername: '',
    autoRenew: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (license) {
      setFormData({
        ownerUsername: '',
        autoRenew: license.autoRenew || false,
      });
    }

    if (open) {
      setError(null);
    }

  }, [license, fieldToEdit, open]);

  const handleSave = () => {
    setLoading(true);
    setError(null);
    
    let requestBody = _.cloneDeep(license);

    requestBody.duration = null;
    requestBody.updatedFor = userData?.username || 'N/A';
    requestBody.ownerUsername = null;
    
    if (fieldToEdit === 'ownerUsername') {
      const trimmedUsername = formData.ownerUsername.trim();

      if (!trimmedUsername) {
        setError('Owner username cannot be blank');
        setLoading(false);
        return;
      }

      requestBody.ownerUsername = trimmedUsername;
          
    } else if (fieldToEdit === 'autoRenew') {
      requestBody.autoRenew = formData.autoRenew;
    }
    
    axios.put(`/api/licenses/${license.id}`, requestBody)
      .then(() => {
        if (onUpdate) {
          onUpdate();
        }
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        console.error('Error updating license:', err);
        setError(err.response?.data?.message || 'Failed to update license. Please try again.');
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {fieldToEdit === 'ownerUsername' ? 'Edit Owner Username' : 'Edit Auto Renew Setting'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {fieldToEdit === 'ownerUsername' && (
          <TextField
            label="Owner Username"
            onChange={(e) => setFormData({...formData, ownerUsername: e.target.value})}
            fullWidth
            margin="normal"
            disabled={loading}
          />
        )}
        
        {fieldToEdit === 'autoRenew' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Auto Renew
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant={formData.autoRenew === "YES" ? "contained" : "outlined"}
                onClick={() => setFormData({...formData, autoRenew: "YES"})}
                disabled={loading}
                sx={{ 
                  minWidth: '80px',
                  bgcolor: formData.autoRenew === "YES" ? "primary.main" : "transparent",
                  color: formData.autoRenew === "YES" ? "white" : "primary.main",
                  '&:hover': {
                    bgcolor: formData.autoRenew === "YES" ? "primary.dark" : "rgba(63, 81, 181, 0.08)"
                  }
                }}
              >
                YES
              </Button>
              <Button 
                variant={formData.autoRenew === "NO" ? "contained" : "outlined"}
                onClick={() => setFormData({...formData, autoRenew: "NO"})}
                disabled={loading}
                sx={{ 
                  minWidth: '80px',
                  bgcolor: formData.autoRenew === "NO" ? "primary.main" : "transparent",
                  color: formData.autoRenew === "NO" ? "white" : "primary.main",
                  '&:hover': {
                    bgcolor: formData.autoRenew === "NO" ? "primary.dark" : "rgba(63, 81, 181, 0.08)"
                  }
                }}
              >
                NO
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={loading || (fieldToEdit === 'ownerUsername' && !formData.ownerUsername)}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}